/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import HousienBrandLogo from '@/shared/ui/components/basic/logos/HousienBrandLogo';
import { Text } from '@/shared/ui';
import {
  PublicProperty,
  getCategoryDisplay,
  formatBRL,
  publicFetch,
  toPublicPropertyView,
} from './PropertyPublicView.types';
import { Lightbox } from './PropertyPublicView.atoms';
import { PropertyLoadingState, PropertyUnavailableState } from './PropertyPublicView.states';
import { PropertyGallery } from './PropertyPublicView.gallery';
import { PropertyInfoPanel } from './PropertyPublicView.info';

/**
 * Resolves the public API base URL from runtime env (`window.__ENV__`), falling
 * back to Vite build-time env, then an empty string. Used for the unauthenticated
 * public-property fetch which has no configured API client.
 *
 * @returns The resolved API base URL, or an empty string when unset.
 */
function resolvePublicApiBase(): string {
  const windowEnv = (window as any).__ENV__;
  return (
    windowEnv?.REACT_APP_API_URL ||
    windowEnv?.REACT_APP_HOUSIEN_BACKEND_URL ||
    import.meta.env.REACT_APP_API_URL ||
    ''
  );
}

/**
 * Public property listing page for clients (no authentication).
 */
export const PropertyPublicView: React.FC = () => {
  const { agency_slug, property_slug } = useParams<{ agency_slug: string; property_slug: string }>();
  const { t } = useTranslation('@pages/public-property');
  const [property, setProperty] = useState<PublicProperty | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    async function fetchProperty() {
      const fullUrl = `${resolvePublicApiBase()}/public/properties/${agency_slug}/${property_slug}`;
      try {
        const res = await publicFetch(fullUrl);
        if (!res.ok) { setUnavailable(true); return; }
        setProperty(toPublicPropertyView(await res.json()));
      } catch {
        setUnavailable(true);
      } finally {
        setLoading(false);
      }
    }
    fetchProperty();
  }, [agency_slug, property_slug]);

  if (loading) return <PropertyLoadingState />;
  if (unavailable || !property) return <PropertyUnavailableState />;

  const images = property.images || [];
  const salePrice = property.sale?.price;
  const rentalPrice = property.rental?.price;
  const categoryDisplay = getCategoryDisplay(t)[property.category || ''] || property.category;
  const ogPrice = salePrice
    ? formatBRL(salePrice)
    : rentalPrice
      ? `${formatBRL(rentalPrice)}${t('price.perMonth')}`
      : '';

  return (
    <div
      className="min-h-screen bg-(--surface-primary) [font-family:var(--font-family)]"
      data-testid="public-property-view"
    >
      <Helmet>
        <title>{property.property_title} | housien</title>
        <meta property="og:title" content={property.property_title} />
        <meta
          property="og:description"
          content={t('ogDescription', {
            category: categoryDisplay || 'Imóvel',
            city: property.city || '',
            price: ogPrice,
          })}
        />
        {images[0] && <meta property="og:image" content={images[0]} />}
        <meta property="og:type" content="website" />
      </Helmet>

      <header className="flex items-center justify-between border-b border-(--border-subtle) bg-(--surface-primary) px-(--space-5) lg:px-(--space-12)">
        <HousienBrandLogo variant="full" height={72} />
        {property.agency_name && (
          <Text as="span" className="text-[length:var(--text-meta)] font-medium text-(--text-tertiary)">
            {t('header.by', { agency: property.agency_name })}
          </Text>
        )}
      </header>

      <div className="grid min-h-[calc(100vh-73px)] grid-cols-1 gap-(--space-5) p-(--space-5) lg:grid-cols-[2fr_3fr] lg:gap-(--space-8) lg:px-(--space-12)">
        <PropertyGallery images={images} title={property.property_title} onOpenLightbox={setLightboxIndex} />
        <PropertyInfoPanel property={property} />
      </div>

      {lightboxIndex !== null && (
        <Lightbox images={images} startIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} />
      )}
    </div>
  );
};
